"use client";

import { useState, useEffect, Suspense } from "react";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Check } from "lucide-react";

function SignupContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const invitationToken = searchParams.get("invitationToken");
  const invitedEmail = searchParams.get("email");
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (invitedEmail && !email) {
      setEmail(invitedEmail);
    }
  }, [invitedEmail, email]);

  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Password strength
  const getPasswordStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    return strength;
  };

  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];
  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong'];

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords don't match";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    
    if (!validateForm()) return;
    
    setIsLoading(true);

    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name, 
          email, 
          password,
          ...(invitationToken && { invitationToken }),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMsg = data.error || "Something went wrong";
        if (errorMsg.toLowerCase().includes("email") && errorMsg.toLowerCase().includes("exists")) {
          setFieldErrors({ email: "An account with this email already exists" });
        } else if (errorMsg.toLowerCase().includes("email")) {
          setFieldErrors({ email: errorMsg });
        } else if (errorMsg.toLowerCase().includes("password")) {
          setFieldErrors({ password: errorMsg });
        } else {
          setError(errorMsg);
        }
        setIsLoading(false);
        return;
      }

      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        setError("Account created! Please sign in.");
      } else {
        if (data.acceptedInvitation?.workspaceId) {
          router.push(`/dashboard?workspace=${data.acceptedInvitation.workspaceId}`);
        } else {
          router.push("/dashboard");
        }
        router.refresh();
      }
    } catch {
      setError("Unable to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const callbackUrl = invitationToken ? `/invite/${invitationToken}` : "/dashboard";
    signIn("google", { callbackUrl });
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="auth-glass-card rounded-2xl p-5 md:p-8">
      <h2 className="mb-1 md:mb-2 text-center text-lg md:text-xl font-bold text-white">Create your account</h2>
      <p className="mb-4 md:mb-6 text-center text-xs md:text-sm text-gray-400">
        {invitationToken 
          ? "Create an account to accept your invitation"
          : "Start your free trial — no credit card required"}
      </p>

      {invitationToken && invitedEmail && (
        <div className="mb-4 rounded-xl bg-blue-500/10 border border-blue-500/20 p-4 text-sm text-blue-400">
          Creating account to join a workspace. Use <strong>{invitedEmail}</strong>.
        </div>
      )}

      {/* Google Sign Up */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        className="auth-social-btn w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl font-medium mb-6"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </button>

      <div className="mb-6 flex items-center">
        <div className="flex-1 border-t border-white/10"></div>
        <span className="px-4 text-sm text-gray-400">or</span>
        <div className="flex-1 border-t border-white/10"></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div role="alert" className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Name Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <User className="w-5 h-5" />
            </div>
            <input
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError("name");
              }}
              className="auth-input w-full pl-12 pr-4 py-3 rounded-xl text-white"
              autoComplete="name"
            />
          </div>
        </div>

        {/* Email Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <Mail className="w-5 h-5" />
            </div>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError("email");
              }}
              className={`auth-input w-full pl-12 pr-4 py-3 rounded-xl text-white ${fieldErrors.email ? 'border-red-500/50' : ''}`}
              autoComplete="email"
            />
          </div>
          {fieldErrors.email && <p className="mt-2 text-sm text-red-400">{fieldErrors.email}</p>}
        </div>

        {/* Password Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <Lock className="w-5 h-5" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearFieldError("password");
              }}
              className={`auth-input w-full pl-12 pr-12 py-3 rounded-xl text-white ${fieldErrors.password ? 'border-red-500/50' : ''}`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {fieldErrors.password && <p className="mt-2 text-sm text-red-400">{fieldErrors.password}</p>}
          
          {/* Password Strength Indicator */}
          {password && (
            <div className="mt-3">
              <div className="flex gap-1 mb-1">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors ${i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-white/10'}`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-400">
                Password strength: <span className={`font-medium ${passwordStrength > 2 ? 'text-green-400' : passwordStrength > 1 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {strengthLabels[passwordStrength - 1] || 'Too weak'}
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Confirm Password Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <Lock className="w-5 h-5" />
            </div>
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                clearFieldError("confirmPassword");
              }}
              className={`auth-input w-full pl-12 pr-12 py-3 rounded-xl text-white ${fieldErrors.confirmPassword ? 'border-red-500/50' : ''}`}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            {confirmPassword && password === confirmPassword && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2 text-green-400">
                <Check className="w-5 h-5" />
              </div>
            )}
          </div>
          {fieldErrors.confirmPassword && <p className="mt-2 text-sm text-red-400">{fieldErrors.confirmPassword}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="auth-gradient-btn w-full py-3.5 rounded-xl text-white font-bold text-base shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Creating account...
            </>
          ) : (
            <>
              Create Account
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-400">
        Already have an account?{" "}
        <Link href="/login" className="text-purple-400 hover:text-purple-300 font-medium transition-colors">
          Sign in
        </Link>
      </p>

      <p className="mt-4 text-center text-xs text-gray-500">
        By signing up, you agree to our{" "}
        <Link href="/terms" className="text-purple-400/70 hover:text-purple-300">Terms</Link>
        {" "}and{" "}
        <Link href="/privacy" className="text-purple-400/70 hover:text-purple-300">Privacy Policy</Link>
      </p>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={
      <div className="auth-glass-card rounded-2xl p-8 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-white/10 rounded mb-6 mx-auto w-40"></div>
          <div className="h-12 bg-white/10 rounded mb-4"></div>
          <div className="h-12 bg-white/10 rounded mb-4"></div>
          <div className="h-12 bg-white/10 rounded mb-4"></div>
          <div className="h-12 bg-white/10 rounded"></div>
        </div>
      </div>
    }>
      <SignupContent />
    </Suspense>
  );
}
